import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@jambushrushti/integrations/supabase/client";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";
import { useToast } from "@jambushrushti/hooks/use-toast";
import {
  Globe,
  Home,
  Users,
  FileText,
  Settings,
  Save,
  Loader2,
  BookOpen,
  Calendar,
  Image,
  Quote,
} from "lucide-react";

// CMS content managed locally until a CMS settings table is connected.
interface CMSContent {
  heroTitle: string;
  heroSubtitle: string;
  heroDescription: string;
  aboutTitle: string;
  aboutDescription: string;
  missionStatement: string;
  contactEmail: string;
  contactPhone: string;
  contactAddress: string;
  footerText: string;
  socialFacebook: string;
  socialTwitter: string;
  socialYoutube: string;
}

const defaultContent: CMSContent = {
  heroTitle: "",
  heroSubtitle: "",
  heroDescription: "",
  aboutTitle: "",
  aboutDescription: "",
  missionStatement: "",
  contactEmail: "",
  contactPhone: "",
  contactAddress: "",
  footerText: "",
  socialFacebook: "",
  socialTwitter: "",
  socialYoutube: "",
};

const CMS_CONTENT_KEY = "cms_content_v2";

export default function CMSManagement() {
  const { toast } = useToast();
  const [content, setContent] = useState<CMSContent>(() => {
    const saved = localStorage.getItem(CMS_CONTENT_KEY);
    return saved ? JSON.parse(saved) : defaultContent;
  });
  const [isSaving, setIsSaving] = useState(false);

  // Stats from database
  const { data: stats } = useQuery({
    queryKey: ["cms-stats"],
    queryFn: async () => {
      const [books, guruvani, articles, blogs, events, gallery] =
        await Promise.all([
          supabase.from("books").select("id", { count: "exact", head: true }),
          supabase
            .from("guruvani")
            .select("id", { count: "exact", head: true }),
          supabase
            .from("articles")
            .select("id", { count: "exact", head: true }),
          supabase.from("blogs").select("id", { count: "exact", head: true }),
          supabase.from("events").select("id", { count: "exact", head: true }),
          supabase.from("gallery").select("id", { count: "exact", head: true }),
        ]);
      return {
        books: books.count || 0,
        guruvani: guruvani.count || 0,
        articles: articles.count || 0,
        blogs: blogs.count || 0,
        events: events.count || 0,
        gallery: gallery.count || 0,
      };
    },
  });

  const handleSave = () => {
    setIsSaving(true);
    try {
      localStorage.setItem(CMS_CONTENT_KEY, JSON.stringify(content));
      // Dispatch event to notify other components
      window.dispatchEvent(new Event("cms-content-updated"));
      toast({ title: "Settings saved successfully!" });
    } catch (error) {
      toast({ title: "Error saving settings", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setContent(defaultContent);
    localStorage.removeItem("cms_content");
    toast({ title: "Settings reset to defaults" });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Website CMS
            </h1>
            <p className="mt-1 text-muted-foreground">
              Manage homepage, about sections, and public content1
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Button variant="outline" onClick={handleReset} className="w-fit">
              Reset to Defaults
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="w-fit">
              {isSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Content Statistics */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <Card className="shadow-soft rounded-xl border-0">
            <CardContent className="p-4 text-center">
              <BookOpen className="mx-auto mb-2 h-6 w-6 text-primary" />
              <p className="text-2xl font-bold">{stats?.books || 0}</p>
              <p className="text-xs text-muted-foreground">Books</p>
            </CardContent>
          </Card>
          <Card className="shadow-soft rounded-xl border-0">
            <CardContent className="p-4 text-center">
              <Quote className="mx-auto mb-2 h-6 w-6 text-primary" />
              <p className="text-2xl font-bold">{stats?.guruvani || 0}</p>
              <p className="text-xs text-muted-foreground">Guruvani</p>
            </CardContent>
          </Card>
          <Card className="shadow-soft rounded-xl border-0">
            <CardContent className="p-4 text-center">
              <FileText className="mx-auto mb-2 h-6 w-6 text-primary" />
              <p className="text-2xl font-bold">{stats?.articles || 0}</p>
              <p className="text-xs text-muted-foreground">Articles</p>
            </CardContent>
          </Card>
          <Card className="shadow-soft rounded-xl border-0">
            <CardContent className="p-4 text-center">
              <FileText className="mx-auto mb-2 h-6 w-6 text-primary" />
              <p className="text-2xl font-bold">{stats?.blogs || 0}</p>
              <p className="text-xs text-muted-foreground">Blogs</p>
            </CardContent>
          </Card>
          <Card className="shadow-soft rounded-xl border-0">
            <CardContent className="p-4 text-center">
              <Calendar className="mx-auto mb-2 h-6 w-6 text-primary" />
              <p className="text-2xl font-bold">{stats?.events || 0}</p>
              <p className="text-xs text-muted-foreground">Events</p>
            </CardContent>
          </Card>
          <Card className="shadow-soft rounded-xl border-0">
            <CardContent className="p-4 text-center">
              <Image className="mx-auto mb-2 h-6 w-6 text-primary" />
              <p className="text-2xl font-bold">{stats?.gallery || 0}</p>
              <p className="text-xs text-muted-foreground">Gallery</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="homepage" className="space-y-6">
          <div className="w-full overflow-x-auto pb-1">
            <TabsList className="inline-flex w-max rounded-xl bg-muted/50 p-1">
              <TabsTrigger
                value="contact"
                className="gap-2 rounded-lg data-[state=active]:bg-background"
              >
                <FileText className="h-4 w-4" />
                Contact
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="contact" className="space-y-4">
            <Card className="shadow-soft rounded-2xl border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Contact Information
                </CardTitle>
                <CardDescription>
                  Manage contact details displayed on the website
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={content.contactEmail}
                    onChange={e =>
                      setContent({ ...content, contactEmail: e.target.value })
                    }
                    placeholder="contact@example.com"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={content.contactPhone}
                    onChange={e =>
                      setContent({ ...content, contactPhone: e.target.value })
                    }
                    placeholder="+91 12345 67890"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Contact Address</Label>
                  <Textarea
                    value={content.contactAddress}
                    onChange={e =>
                      setContent({ ...content, contactAddress: e.target.value })
                    }
                    placeholder="Full address"
                    rows={2}
                    className="rounded-xl"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
